import React, { VoidFunctionComponent } from 'react';
import {
  CardListItem,
  CardListItemProps
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { CardListItemStatusIcons } from 'record-it-components/cardList/CardListItemStatusIcons/CardListItemStatusIcons';
import { Icon, IconSize } from 'record-it-components/misc/Icon/Icon';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useConstructionSiteDeleteHandler } from '../../../hooks/entity/deleteHandler/useConstructionSiteDeleteHandler';
import { EditConstructionSiteDialog } from '../EditConstructionSiteDialog/EditConstructionSiteDialog';

export type ConstructionSiteListItemProps =
  PropsWithoutChildren<CardListItemProps> & {
    constructionSite: ConstructionSite;
  };

export const ConstructionSiteListItem: VoidFunctionComponent<ConstructionSiteListItemProps> =
  React.memo((props) => {
    const { constructionSite, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const handleDeleteClick =
      useConstructionSiteDeleteHandler(constructionSite);

    const {
      open: openEditDialog,
      close: closeEditDialog,
      modalProps
    } = useModalOpened({ closeOnBackDropClick: true });

    return (
      <>
        <CardListItem {...remainingProps}>
          <CardListItemTitle data-testid="ConstructionSiteListItem.Title">
            {constructionSite.name}
          </CardListItemTitle>
          <CardListItemStatusIcons>
            {constructionSite.archived ? (
              <Icon
                {...iconConfigurations.default.status.archived}
                size={IconSize.Small}
                data-testid="ConstructionSiteListItem.ArchivedStatusIcon"
              ></Icon>
            ) : null}
          </CardListItemStatusIcons>
          <CardListItemIconButton
            {...iconConfigurations.prefixed.listItem.deleteIcon}
            onClick={handleDeleteClick}
            data-testid="ConstructionSiteListItem.DeleteButton"
          ></CardListItemIconButton>
          <CardListItemIconButton
            {...iconConfigurations.prefixed.listItem.editInDialogIcon}
            onClick={openEditDialog}
            data-testid="ConstructionSiteListItem.EditButton"
          ></CardListItemIconButton>
        </CardListItem>
        <EditConstructionSiteDialog
          constructionSite={constructionSite}
          onAccept={closeEditDialog}
          {...modalProps}
          data-testid="ConstructionSiteListItem.EditDialog"
        ></EditConstructionSiteDialog>
      </>
    );
  });
